import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { adminService } from '../../../services/adminService';

const OwnerVerificationCenter = () => {
  const [verificationRequests, setVerificationRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    loadRequests();
  }, [filter]);

  const loadRequests = async () => {
    setLoading(true);
    try {
      const data = await adminService?.getVerificationRequests(filter);
      setVerificationRequests(data);
    } catch (error) {
      console.error('Error loading verification requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleApprove = async (userId) => {
    setActionLoading(true);
    try {
      await adminService?.approveVerification(userId);
      setSelectedRequest(null);
      await loadRequests();
      showToast('Owner verification approved');
    } catch (error) {
      showToast('Failed to approve verification', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (userId) => {
    setActionLoading(true);
    try {
      await adminService?.rejectVerification(userId);
      setSelectedRequest(null);
      await loadRequests();
      showToast('Owner verification rejected');
    } catch (error) {
      showToast('Failed to reject verification', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'pending': return { label: 'Pending', color: 'bg-warning/10 text-warning', icon: 'Clock' };
      case 'under_review': return { label: 'Under Review', color: 'bg-primary/10 text-primary', icon: 'Eye' };
      case 'verified': return { label: 'Verified', color: 'bg-success/10 text-success', icon: 'CheckCircle' };
      case 'approved': return { label: 'Approved', color: 'bg-success/10 text-success', icon: 'CheckCircle' };
      case 'rejected': return { label: 'Rejected', color: 'bg-error/10 text-error', icon: 'XCircle' };
      default: return { label: 'Unverified', color: 'bg-muted text-muted-foreground', icon: 'AlertCircle' };
    }
  };

  const pendingCount = verificationRequests?.filter(r => r?.verificationStatus === 'pending' || !r?.verificationStatus)?.length;
  const underReviewCount = verificationRequests?.filter(r => r?.verificationStatus === 'under_review')?.length;

  return (
    <div className="space-y-6">
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg font-body text-sm font-medium ${
          toast?.type === 'error' ? 'bg-error text-white' : 'bg-success text-white'
        }`}>
          {toast?.message}
        </div>
      )}
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-card rounded-lg shadow-elevation-2 p-5">
          <div className="flex items-center justify-between mb-2">
            <div className="w-10 h-10 bg-warning/10 rounded-full flex items-center justify-center">
              <Icon name="Clock" size={20} strokeWidth={2} className="text-warning" />
            </div>
          </div>
          <p className="font-heading text-2xl font-bold text-foreground mb-1">{pendingCount}</p>
          <p className="font-caption text-sm text-muted-foreground">Pending Review</p>
        </div>
        <div className="bg-card rounded-lg shadow-elevation-2 p-5">
          <div className="flex items-center justify-between mb-2">
            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
              <Icon name="Eye" size={20} strokeWidth={2} className="text-primary" />
            </div>
          </div>
          <p className="font-heading text-2xl font-bold text-foreground mb-1">{underReviewCount}</p>
          <p className="font-caption text-sm text-muted-foreground">Under Review</p>
        </div>
        <div className="bg-card rounded-lg shadow-elevation-2 p-5">
          <div className="flex items-center justify-between mb-2">
            <div className="w-10 h-10 bg-success/10 rounded-full flex items-center justify-center">
              <Icon name="CheckCircle" size={20} strokeWidth={2} className="text-success" />
            </div>
          </div>
          <p className="font-heading text-2xl font-bold text-foreground mb-1">
            {verificationRequests?.filter(r => r?.verificationStatus === 'verified' || r?.isVerified)?.length}
          </p>
          <p className="font-caption text-sm text-muted-foreground">Verified Owners</p>
        </div>
        <div className="bg-card rounded-lg shadow-elevation-2 p-5">
          <div className="flex items-center justify-between mb-2">
            <div className="w-10 h-10 bg-error/10 rounded-full flex items-center justify-center">
              <Icon name="XCircle" size={20} strokeWidth={2} className="text-error" />
            </div>
          </div>
          <p className="font-heading text-2xl font-bold text-foreground mb-1">
            {verificationRequests?.filter(r => r?.verificationStatus === 'rejected')?.length}
          </p>
          <p className="font-caption text-sm text-muted-foreground">Rejected</p>
        </div>
      </div>
      {/* Filter Tabs */}
      <div className="bg-card rounded-lg shadow-elevation-2 p-2 flex items-center justify-between">
        <div className="flex space-x-2 flex-wrap gap-1">
          {['all', 'pending', 'under_review', 'verified', 'rejected']?.map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-4 py-2 rounded-md font-body text-sm font-medium transition-smooth capitalize ${
                filter === status ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'
              }`}
            >
              {status?.replace('_', ' ')}
            </button>
          ))}
        </div>
        <Button variant="outline" size="sm" onClick={loadRequests}>
          <Icon name="RefreshCw" size={14} strokeWidth={2} className="mr-1" />
          Refresh
        </Button>
      </div>
      {/* Verification Requests List */}
      <div className="bg-card rounded-lg shadow-elevation-2">
        <div className="p-6 border-b border-border">
          <h3 className="font-heading text-lg font-semibold text-foreground flex items-center space-x-2">
            <Icon name="ShieldCheck" size={20} strokeWidth={2} className="text-primary" />
            <span>Owner Accounts ({verificationRequests?.length})</span>
          </h3>
        </div>
        <div className="divide-y divide-border">
          {loading ? (
            <div className="p-8 text-center">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-3" />
              <p className="font-body text-muted-foreground">Loading verification requests...</p>
            </div>
          ) : verificationRequests?.length === 0 ? (
            <div className="p-8 text-center">
              <Icon name="Inbox" size={48} strokeWidth={1.5} className="text-muted-foreground mx-auto mb-3" />
              <p className="font-body text-muted-foreground">No verification requests found</p>
            </div>
          ) : (
            verificationRequests?.map((request) => {
              const statusConfig = getStatusConfig(request?.verificationStatus);
              return (
                <div key={request?.id} className="p-5 hover:bg-muted/50 transition-smooth">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center flex-wrap gap-2 mb-2">
                        <h4 className="font-body font-semibold text-foreground">{request?.fullName || 'Unknown Owner'}</h4>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusConfig?.color} flex items-center space-x-1`}>
                          <Icon name={statusConfig?.icon} size={12} strokeWidth={2} />
                          <span>{statusConfig?.label}</span>
                        </span>
                      </div>
                      <p className="font-body text-sm text-muted-foreground mb-2">{request?.email}</p>
                      <div className="flex items-center flex-wrap gap-3 text-xs text-muted-foreground">
                        <span className="flex items-center space-x-1">
                          <Icon name="Calendar" size={12} strokeWidth={2} />
                          <span>Joined {new Date(request?.createdAt)?.toLocaleDateString()}</span>
                        </span>
                        {request?.phone && (
                          <span className="flex items-center space-x-1">
                            <Icon name="Phone" size={12} strokeWidth={2} />
                            <span>{request?.phone}</span>
                          </span>
                        )}
                        {request?.locationCity && (
                          <span className="flex items-center space-x-1">
                            <Icon name="MapPin" size={12} strokeWidth={2} />
                            <span>{request?.locationCity}, {request?.locationState}</span>
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedRequest(request)}
                      >
                        <Icon name="Eye" size={14} strokeWidth={2} />
                        <span className="ml-1">Review</span>
                      </Button>
                      {(!request?.verificationStatus || request?.verificationStatus === 'pending' || request?.verificationStatus === 'under_review') && (
                        <>
                          <Button
                            variant="success"
                            size="sm"
                            disabled={actionLoading}
                            onClick={() => handleApprove(request?.id)}
                          >
                            <Icon name="Check" size={14} strokeWidth={2} />
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            disabled={actionLoading}
                            onClick={() => handleReject(request?.id)}
                          >
                            <Icon name="X" size={14} strokeWidth={2} />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
      {/* Owner Detail Modal */}
      {selectedRequest && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card rounded-xl shadow-elevation-3 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border flex items-center justify-between">
              <h3 className="font-heading text-lg font-semibold text-foreground">Owner Profile Review</h3>
              <button onClick={() => setSelectedRequest(null)} className="text-muted-foreground hover:text-foreground">
                <Icon name="X" size={20} strokeWidth={2} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="font-body text-xs text-muted-foreground mb-1">Full Name</p>
                  <p className="font-body text-sm font-semibold text-foreground">{selectedRequest?.fullName || 'N/A'}</p>
                </div>
                <div>
                  <p className="font-body text-xs text-muted-foreground mb-1">Email</p>
                  <p className="font-body text-sm text-foreground">{selectedRequest?.email || 'N/A'}</p>
                </div>
                <div>
                  <p className="font-body text-xs text-muted-foreground mb-1">Phone</p>
                  <p className="font-body text-sm text-foreground">{selectedRequest?.phone || 'N/A'}</p>
                </div>
                <div>
                  <p className="font-body text-xs text-muted-foreground mb-1">Location</p>
                  <p className="font-body text-sm text-foreground">
                    {selectedRequest?.locationCity ? `${selectedRequest?.locationCity}, ${selectedRequest?.locationState}` : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="font-body text-xs text-muted-foreground mb-1">Verification Status</p>
                  <p className="font-body text-sm font-semibold text-foreground capitalize">
                    {selectedRequest?.verificationStatus || 'Unverified'}
                  </p>
                </div>
                <div>
                  <p className="font-body text-xs text-muted-foreground mb-1">Member Since</p>
                  <p className="font-body text-sm text-foreground">
                    {new Date(selectedRequest?.createdAt)?.toLocaleDateString()}
                  </p>
                </div>
              </div>
              {(!selectedRequest?.verificationStatus || selectedRequest?.verificationStatus === 'pending' || selectedRequest?.verificationStatus === 'under_review') && (
                <div className="flex items-center justify-end space-x-3 pt-4 border-t border-border">
                  <Button
                    variant="danger"
                    disabled={actionLoading}
                    onClick={() => handleReject(selectedRequest?.id)}
                  >
                    <Icon name="XCircle" size={16} strokeWidth={2} className="mr-2" />
                    Reject
                  </Button>
                  <Button
                    variant="success"
                    disabled={actionLoading}
                    onClick={() => handleApprove(selectedRequest?.id)}
                  >
                    <Icon name="CheckCircle" size={16} strokeWidth={2} className="mr-2" />
                    Approve
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OwnerVerificationCenter;